/**
 * AdminFoundingLightReservations — Feb 2026
 *
 * Visual panel at /admin/founding-light-reservations. Every visitor
 * who taps "Reserve my Founding Light" on soulumo.app lands here —
 * newest first, with optional note, email, and a status chip
 * (new / contacted / paid / declined). When Stripe is live, this
 * is the warm list to convert.
 */
import { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { AdminLayout } from "../AdminPages";
import {
  ArrowLeft, Sparkles, Loader2, Copy, Check, MessageCircle, Trash2, Send, Rocket,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;
const auth = () => ({ Authorization: `Bearer ${localStorage.getItem("reclaim_token")}` });

const STATUSES = ["new", "contacted", "paid", "declined"];
const STATUS_STYLE = {
  new:       { bg: "bg-amber-50",   fg: "text-amber-700",   bd: "border-amber-200" },
  contacted: { bg: "bg-sky-50",     fg: "text-sky-700",     bd: "border-sky-200" },
  paid:      { bg: "bg-emerald-50", fg: "text-emerald-700", bd: "border-emerald-200" },
  declined:  { bg: "bg-slate-100",  fg: "text-slate-600",   bd: "border-slate-300" },
};

export default function AdminFoundingLightReservations() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({ new: 0, contacted: 0, paid: 0, declined: 0 });
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [filter, setFilter] = useState("all");
  const [copied, setCopied] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [blastModal, setBlastModal] = useState(null); // null | {dryRunResult} | {sending} | {result}

  const load = async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API}/api/admin/founding-light-reservations`, { headers: auth() });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const d = await r.json();
      setItems(d.items || []);
      setCounts(d.counts || {});
    } catch (e) {
      setErr(String(e.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const setStatus = async (id, status) => {
    try {
      await fetch(`${API}/api/admin/founding-light-reservations/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...auth() },
        body: JSON.stringify({ status }),
      });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Remove ${name || "this reservation"}?`)) return;
    try {
      await fetch(`${API}/api/admin/founding-light-reservations/${id}`, { method: "DELETE", headers: auth() });
      await load();
    } catch (e) {
      setErr(String(e.message || e));
    }
  };

  const copy = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    } catch (_) { /* clipboard blocked — text visible on screen */ }
  };

  const sendLaunchEmailOne = async (id, name) => {
    if (!window.confirm(`Send the launch email to ${name || "this reserver"} now?`)) return;
    setSendingId(id);
    try {
      const r = await fetch(`${API}/api/admin/founding-light-reservations/${id}/send-launch-email`, {
        method: "POST",
        headers: auth(),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.detail || `HTTP ${r.status}`);
      }
      await load();
    } catch (e) {
      alert(`Could not send: ${e.message}`);
    } finally {
      setSendingId(null);
    }
  };

  const previewBlast = async () => {
    setBlastModal({ loading: true });
    try {
      const r = await fetch(`${API}/api/admin/founding-light-reservations/launch-blast?dry_run=true`, {
        method: "POST",
        headers: auth(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setBlastModal({ preview: d });
    } catch (e) {
      setBlastModal(null);
      alert(`Preview failed: ${e.message}`);
    }
  };

  const sendBlast = async () => {
    if (!window.confirm("Send the launch email to every un-emailed reservation. Continue?")) return;
    setBlastModal({ sending: true });
    try {
      const r = await fetch(`${API}/api/admin/founding-light-reservations/launch-blast`, {
        method: "POST",
        headers: auth(),
      });
      const d = await r.json();
      if (!r.ok) throw new Error(d.detail || `HTTP ${r.status}`);
      setBlastModal({ result: d });
      await load();
    } catch (e) {
      setBlastModal(null);
      alert(`Blast failed: ${e.message}`);
    }
  };

  const whatsappMessage = (it) => {
    const first = (it.name || "").split(" ")[0] || "there";
    const noteHint = it.note
      ? `\n\nYou wrote: "${it.note.slice(0, 80)}${it.note.length > 80 ? "…" : ""}"\n`
      : "";
    return `Hi ${first} — Michael here. Thank you for reserving your Founding Light place on Soulumo.${noteHint}\n\nWe've just opened the doors. Your 80% Founding Light rate is locked in — £19.99 lifetime + £2.99 / month. Here's the link to claim it: https://soulumo.app/upgrade\n\nMichael 🙏`;
  };

  const filtered = useMemo(() => {
    if (filter === "all") return items;
    return items.filter((it) => (it.status || "new") === filter);
  }, [items, filter]);

  const total = items.length;

  return (
    <AdminLayout title="Founding Light — reservations">
      <div className="mb-6 flex items-center justify-between" data-testid="admin-founding-light">
        <button
          type="button"
          onClick={() => nav("/admin")}
          className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900"
          data-testid="founding-light-back"
        >
          <ArrowLeft className="w-4 h-4" /> Back to admin
        </button>
        <button
          type="button"
          onClick={load}
          className="text-sm text-slate-500 hover:text-slate-900"
          data-testid="founding-light-refresh"
        >
          Refresh
        </button>
      </div>

      <div className="mb-6 rounded-xl border border-slate-200 bg-white p-5">
        <div className="flex items-start gap-3">
          <div className="rounded-full bg-amber-50 text-amber-700 p-2 flex-shrink-0">
            <Sparkles className="w-5 h-5" />
          </div>
          <div>
            <h2 className="font-semibold text-slate-900">Warm list for launch day</h2>
            <p className="text-sm text-slate-500 mt-1">
              Every visitor who tapped <em>Reserve my Founding Light</em> on
              soulumo.app lands here. The moment Stripe is live, flip them
              from <em>new</em> → <em>contacted</em> → <em>paid</em>.
            </p>
          </div>
        </div>
      </div>

      {/* Launch-day blast banner — hidden if nothing to send */}
      {(counts.new || 0) > 0 && (
        <div
          className="mb-6 rounded-xl border border-amber-200 bg-gradient-to-br from-amber-50 to-orange-50 p-5"
          data-testid="founding-light-blast-banner"
        >
          <div className="flex items-start gap-3">
            <div className="rounded-full bg-amber-500 text-white p-2 flex-shrink-0">
              <Rocket className="w-5 h-5" />
            </div>
            <div className="flex-1 min-w-0">
              <h2 className="font-semibold text-slate-900">
                Ready to open the doors?
              </h2>
              <p className="text-sm text-slate-600 mt-1">
                <strong>{counts.new || 0}</strong> reserver{(counts.new || 0) === 1 ? "" : "s"} haven&apos;t been emailed yet.
                Send them a personal "Founding Light is open" note with a one-tap claim link.
                Each row gets marked <em>contacted</em>; nobody gets double-emailed.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={previewBlast}
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-full border border-amber-300 bg-white text-amber-800 hover:bg-amber-50"
                  data-testid="founding-light-preview-blast"
                >
                  <Send className="w-3.5 h-3.5" />
                  Preview recipients
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="mb-5 flex flex-wrap items-center gap-2" data-testid="founding-light-filters">
        <FilterChip
          active={filter === "all"}
          onClick={() => setFilter("all")}
          label="All"
          count={total}
          testid="founding-light-filter-all"
        />
        {STATUSES.map((s) => (
          <FilterChip
            key={s}
            active={filter === s}
            onClick={() => setFilter(s)}
            label={s.charAt(0).toUpperCase() + s.slice(1)}
            count={counts[s] || 0}
            testid={`founding-light-filter-${s}`}
          />
        ))}
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : err ? (
        <div className="text-sm text-red-600" data-testid="founding-light-error">{err}</div>
      ) : filtered.length === 0 ? (
        <div
          className="rounded-xl border border-dashed border-slate-300 bg-slate-50 p-8 text-sm text-slate-500 text-center"
          data-testid="founding-light-empty"
        >
          {total === 0
            ? "No reservations yet. The pricing card is live on soulumo.app — the first \"Reserve my place\" taps will land here."
            : `No reservations with status "${filter}".`}
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((it) => {
            const style = STATUS_STYLE[it.status || "new"] || STATUS_STYLE.new;
            const waText = whatsappMessage(it);
            const waHref = `https://wa.me/?text=${encodeURIComponent(waText)}`;
            return (
              <div
                key={it.id}
                className="rounded-2xl border border-slate-200 bg-white p-4 space-y-3"
                data-testid={`founding-light-row-${it.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <div className="font-semibold text-slate-900 truncate">
                      {it.name || it.email}
                    </div>
                    <a
                      href={`mailto:${it.email}`}
                      className="text-sm text-sky-600 hover:underline truncate block"
                      data-testid={`founding-light-email-${it.id}`}
                    >
                      {it.email}
                    </a>
                    <div className="text-xs text-slate-400 mt-1">
                      {new Date(it.created_at).toLocaleString()}
                      {it.source ? ` · ${it.source}` : ""}
                      {it.launch_email_sent_at && (
                        <span className="ml-1 text-emerald-600">
                          · ✉ emailed {new Date(it.launch_email_sent_at).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                  <span
                    className={`text-xs px-2 py-0.5 rounded-full border ${style.bg} ${style.fg} ${style.bd} capitalize flex-shrink-0`}
                    data-testid={`founding-light-status-${it.id}`}
                  >
                    {it.status || "new"}
                  </span>
                </div>

                {it.note && (
                  <blockquote
                    className="border-l-2 border-amber-300 pl-3 text-sm text-slate-600 italic"
                    data-testid={`founding-light-note-${it.id}`}
                  >
                    "{it.note}"
                  </blockquote>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  {STATUSES.filter((s) => s !== (it.status || "new")).map((s) => (
                    <button
                      key={s}
                      type="button"
                      onClick={() => setStatus(it.id, s)}
                      className="text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50 capitalize"
                      data-testid={`founding-light-setstatus-${it.id}-${s}`}
                    >
                      → {s}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => sendLaunchEmailOne(it.id, it.name)}
                    disabled={sendingId === it.id}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-60"
                    data-testid={`founding-light-sendemail-${it.id}`}
                    title={it.launch_email_sent_at ? `Already sent ${new Date(it.launch_email_sent_at).toLocaleString()} — tap to re-send` : "Send the launch email"}
                  >
                    {sendingId === it.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Send className="w-3.5 h-3.5" />
                    )}
                    {it.launch_email_sent_at ? "Re-send" : "Send launch email"}
                  </button>
                  <button
                    type="button"
                    onClick={() => copy(waText, `wa-${it.id}`)}
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
                    data-testid={`founding-light-copy-${it.id}`}
                  >
                    {copied === `wa-${it.id}`
                      ? <Check className="w-3.5 h-3.5 text-emerald-500" />
                      : <Copy className="w-3.5 h-3.5" />}
                    Copy msg
                  </button>
                  <a
                    href={waHref}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-emerald-600 text-white hover:bg-emerald-700"
                    data-testid={`founding-light-wa-${it.id}`}
                  >
                    <MessageCircle className="w-3.5 h-3.5" />
                    WhatsApp
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(it.id, it.name)}
                    className="ml-auto text-slate-400 hover:text-red-600"
                    aria-label="Remove"
                    data-testid={`founding-light-remove-${it.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Launch blast preview / confirmation modal */}
      {blastModal && (
        <div
          className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-0 sm:p-5"
          style={{ background: "rgba(0,0,0,0.5)" }}
          onClick={() => blastModal.sending ? null : setBlastModal(null)}
          data-testid="founding-light-blast-modal"
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-lg rounded-t-2xl sm:rounded-2xl bg-white p-6 max-h-[90vh] overflow-y-auto"
          >
            {blastModal.loading && (
              <div className="flex items-center gap-2 text-slate-500 py-4">
                <Loader2 className="w-4 h-4 animate-spin" /> Loading recipients…
              </div>
            )}

            {blastModal.preview && (
              <>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  Preview — {blastModal.preview.would_send} launch email{blastModal.preview.would_send === 1 ? "" : "s"}
                </h3>
                <p className="text-sm text-slate-500 mb-4">
                  Each person receives a personal "Founding Light is open" email from
                  <strong> hello@soulumo.app</strong> with a one-tap claim link
                  (<code className="text-xs">{process.env.REACT_APP_FOUNDING_LIGHT_CLAIM_URL || "https://soulumo.app/soulumo"}</code>).
                  Their status flips to <em>contacted</em> on success.
                </p>
                {blastModal.preview.would_send === 0 ? (
                  <div className="rounded-lg bg-slate-50 border border-slate-200 p-4 text-sm text-slate-500 italic">
                    Everyone has already been emailed. Nothing to send.
                  </div>
                ) : (
                  <ul className="space-y-1.5 mb-5 max-h-64 overflow-y-auto border border-slate-200 rounded-lg p-3 bg-slate-50">
                    {blastModal.preview.recipients.map((r) => (
                      <li key={r.id} className="text-sm text-slate-700 flex items-center gap-2">
                        <span className="font-medium">{r.name || "Friend"}</span>
                        <span className="text-slate-400">·</span>
                        <span className="text-slate-500 truncate">{r.email}</span>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setBlastModal(null)}
                    className="text-sm px-4 py-2 rounded-full border border-slate-300 text-slate-600 hover:bg-slate-50"
                    data-testid="founding-light-blast-cancel"
                  >
                    Cancel
                  </button>
                  {blastModal.preview.would_send > 0 && (
                    <button
                      type="button"
                      onClick={sendBlast}
                      className="inline-flex items-center gap-1.5 text-sm px-5 py-2 rounded-full bg-amber-500 text-white hover:bg-amber-600 font-medium"
                      data-testid="founding-light-blast-send"
                    >
                      <Rocket className="w-4 h-4" />
                      Send to all {blastModal.preview.would_send}
                    </button>
                  )}
                </div>
              </>
            )}

            {blastModal.sending && (
              <div className="py-6 text-center">
                <Loader2 className="w-6 h-6 animate-spin text-amber-500 mx-auto" />
                <p className="text-sm text-slate-600 mt-3">Sending launch emails…</p>
                <p className="text-xs text-slate-400 mt-1">Please don&apos;t close this.</p>
              </div>
            )}

            {blastModal.result && (
              <>
                <h3 className="text-lg font-semibold text-slate-900 mb-1">
                  Blast complete
                </h3>
                <div className="mt-3 space-y-2 text-sm">
                  <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-3">
                    <strong className="text-emerald-700">
                      ✉ Sent: {blastModal.result.sent} / {blastModal.result.total}
                    </strong>
                  </div>
                  {blastModal.result.failed && blastModal.result.failed.length > 0 && (
                    <div className="rounded-lg bg-red-50 border border-red-200 p-3">
                      <div className="font-medium text-red-700 mb-1">
                        Failed ({blastModal.result.failed.length}):
                      </div>
                      <ul className="text-xs text-red-600 space-y-0.5">
                        {blastModal.result.failed.map((f, i) => (
                          <li key={i}>{f.email}: {f.error}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
                <div className="mt-5 flex justify-end">
                  <button
                    type="button"
                    onClick={() => setBlastModal(null)}
                    className="text-sm px-4 py-2 rounded-full bg-slate-900 text-white hover:bg-slate-800"
                    data-testid="founding-light-blast-done"
                  >
                    Done
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </AdminLayout>
  );
}

function FilterChip({ active, onClick, label, count, testid }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-xs border transition ${
        active
          ? "bg-slate-900 text-white border-slate-900"
          : "bg-white text-slate-600 border-slate-300 hover:bg-slate-50"
      }`}
    >
      <span>{label}</span>
      <span
        className={`rounded-full px-1.5 py-0.5 text-[10px] font-semibold ${
          active ? "bg-white/20" : "bg-slate-100 text-slate-500"
        }`}
      >
        {count}
      </span>
    </button>
  );
}
